import { MetalsTaskType } from "../enums/metals-task-type.enum";
import { MetalsProjectFile } from "../models/metals-project-file.model";
import {
    MetalsTask,
    MetalsTask_Laser,
    MetalsTask_PowderCoating,
    MetalsTask_PressBrake,
} from "../models/metals-task.model";

export interface MetalsProjectTemplateTaskSummary {
    taskId: string;
    label: string;
    taskType: MetalsTaskType;
    files: string[];
}

export interface MetalsProjectTemplateFieldDiff {
    label: string;
    templateValue: string;
    projectValue: string;
}

export interface MetalsProjectTemplateFileDiff {
    added: string[];
    removed: string[];
    changed: string[];
}

export interface MetalsProjectTemplateTaskDiff {
    templateTaskId: string;
    projectTaskId: string;
    templateTaskLabel: string;
    projectTaskLabel: string;
    taskType: MetalsTaskType;
    fieldChanges: MetalsProjectTemplateFieldDiff[];
    fileChanges: MetalsProjectTemplateFileDiff;
}

export interface MetalsProjectTemplateLinkDiff {
    templateTaskId: string;
    projectTaskId: string;
    taskLabel: string;
    templateNextTaskLabel: string;
    projectNextTaskLabel: string;
}

export interface MetalsProjectTemplateDiff {
    hasChanges: boolean;
    addedTasks: MetalsProjectTemplateTaskSummary[];
    removedTasks: MetalsProjectTemplateTaskSummary[];
    changedTasks: MetalsProjectTemplateTaskDiff[];
    changedLinks: MetalsProjectTemplateLinkDiff[];
}

interface TaskContext {
    nextTaskType: string;
    predecessorTaskTypes: string;
}

interface MatchedTaskPair {
    templateTask: MetalsTask;
    projectTask: MetalsTask;
    score: number;
}

export function diffMetalsProjectTasksAgainstTemplate(
    templateTasks: MetalsTask[],
    projectTasks: MetalsTask[],
): MetalsProjectTemplateDiff {
    const pairs = matchTasks(templateTasks, projectTasks);
    const templateIdToProjectTask = new Map<string, MetalsTask>();
    const projectIdToTemplateTask = new Map<string, MetalsTask>();

    pairs.forEach(pair => {
        templateIdToProjectTask.set(pair.templateTask.id, pair.projectTask);
        projectIdToTemplateTask.set(pair.projectTask.id, pair.templateTask);
    });

    const addedTasks = projectTasks
        .filter(task => !projectIdToTemplateTask.has(task.id))
        .map(toTaskSummary);

    const removedTasks = templateTasks
        .filter(task => !templateIdToProjectTask.has(task.id))
        .map(toTaskSummary);

    const changedTasks = pairs
        .map(pair => toTaskDiff(pair.templateTask, pair.projectTask))
        .filter(diff => diff.fieldChanges.length > 0 || hasFileChanges(diff.fileChanges));

    const changedLinks = pairs
        .map(pair => toLinkDiff(pair.templateTask, pair.projectTask, templateTasks, projectTasks, templateIdToProjectTask, projectIdToTemplateTask))
        .filter((diff): diff is MetalsProjectTemplateLinkDiff => !!diff);

    return {
        hasChanges: addedTasks.length > 0 ||
            removedTasks.length > 0 ||
            changedTasks.length > 0 ||
            changedLinks.length > 0,
        addedTasks,
        removedTasks,
        changedTasks,
        changedLinks,
    };
}

function matchTasks(templateTasks: MetalsTask[], projectTasks: MetalsTask[]): MatchedTaskPair[] {
    const templateContexts = toTaskContextDict(templateTasks);
    const projectContexts = toTaskContextDict(projectTasks);

    const candidates: MatchedTaskPair[] = [];
    for (const templateTask of templateTasks) {
        for (const projectTask of projectTasks) {
            const score = scoreTaskPair(
                templateTask,
                projectTask,
                templateContexts.get(templateTask.id),
                projectContexts.get(projectTask.id),
            );
            if (score >= 100) {
                candidates.push({ templateTask, projectTask, score });
            }
        }
    }

    candidates.sort((a, b) => b.score - a.score);

    const matchedTemplateTaskIds = new Set<string>();
    const matchedProjectTaskIds = new Set<string>();
    const pairs: MatchedTaskPair[] = [];

    for (const candidate of candidates) {
        if (matchedTemplateTaskIds.has(candidate.templateTask.id) || matchedProjectTaskIds.has(candidate.projectTask.id)) {
            continue;
        }

        matchedTemplateTaskIds.add(candidate.templateTask.id);
        matchedProjectTaskIds.add(candidate.projectTask.id);
        pairs.push(candidate);
    }

    return pairs;
}

function scoreTaskPair(
    templateTask: MetalsTask,
    projectTask: MetalsTask,
    templateContext: TaskContext | undefined,
    projectContext: TaskContext | undefined,
): number {
    if (templateTask.type !== projectTask.type) return -1;

    let score = 100;
    if (templateTask.details === projectTask.details) score += 20;
    if (templateTask.translatedDetails === projectTask.translatedDetails) score += 15;
    if (templateTask.activationCondition === projectTask.activationCondition) score += 10;
    if (toHardwareSignature(templateTask) === toHardwareSignature(projectTask)) score += 15;
    if (toTypeSpecificSignature(templateTask) === toTypeSpecificSignature(projectTask)) score += 15;
    if (toFileMatchingSignature(templateTask.files) === toFileMatchingSignature(projectTask.files)) score += 15;
    if (templateContext?.nextTaskType === projectContext?.nextTaskType) score += 10;
    if (templateContext?.predecessorTaskTypes === projectContext?.predecessorTaskTypes) score += 10;

    return score;
}

function toTaskContextDict(tasks: MetalsTask[]): Map<string, TaskContext> {
    const taskDict = new Map<string, MetalsTask>(tasks.map(task => [task.id, task]));
    const predecessorsByTaskId = new Map<string, MetalsTask[]>();

    for (const task of tasks) {
        if (!task.nextTaskId) continue;
        const current = predecessorsByTaskId.get(task.nextTaskId) ?? [];
        current.push(task);
        predecessorsByTaskId.set(task.nextTaskId, current);
    }

    return new Map(tasks.map(task => {
        const nextTask = task.nextTaskId ? taskDict.get(task.nextTaskId) : null;
        const predecessors = predecessorsByTaskId.get(task.id) ?? [];
        const context: TaskContext = {
            nextTaskType: nextTask?.type ?? 'end',
            predecessorTaskTypes: predecessors.map(predecessor => predecessor.type).sort().join('|'),
        };
        return [task.id, context];
    }));
}

function toTaskDiff(templateTask: MetalsTask, projectTask: MetalsTask): MetalsProjectTemplateTaskDiff {
    const fieldChanges: MetalsProjectTemplateFieldDiff[] = [];

    addFieldChange(fieldChanges, 'notes', templateTask.details, projectTask.details);
    addFieldChange(fieldChanges, 'translated notes', templateTask.translatedDetails, projectTask.translatedDetails);
    addFieldChange(fieldChanges, 'activation condition', templateTask.activationCondition, projectTask.activationCondition);
    addFieldChange(fieldChanges, 'hardware items', toHardwareSignature(templateTask), toHardwareSignature(projectTask));

    switch (templateTask.type) {
        case MetalsTaskType.Laser:
            addFieldChange(fieldChanges, 'laser material', (templateTask as MetalsTask_Laser).materialId, (projectTask as MetalsTask_Laser).materialId);
            addFieldChange(fieldChanges, 'approximate width', (templateTask as MetalsTask_Laser).approximateWidth, (projectTask as MetalsTask_Laser).approximateWidth);
            addFieldChange(fieldChanges, 'approximate height', (templateTask as MetalsTask_Laser).approximateHeight, (projectTask as MetalsTask_Laser).approximateHeight);
            break;
        case MetalsTaskType.PressBrake:
            addFieldChange(fieldChanges, 'bend radii', toSortedString((templateTask as MetalsTask_PressBrake).bendRadiusIds), toSortedString((projectTask as MetalsTask_PressBrake).bendRadiusIds));
            break;
        case MetalsTaskType.PowderCoating:
            addFieldChange(fieldChanges, 'powder', (templateTask as MetalsTask_PowderCoating).powderId, (projectTask as MetalsTask_PowderCoating).powderId);
            break;
    }

    return {
        templateTaskId: templateTask.id,
        projectTaskId: projectTask.id,
        templateTaskLabel: toTaskLabel(templateTask),
        projectTaskLabel: toTaskLabel(projectTask),
        taskType: templateTask.type,
        fieldChanges,
        fileChanges: diffFiles(templateTask.files, projectTask.files),
    };
}

function toLinkDiff(
    templateTask: MetalsTask,
    projectTask: MetalsTask,
    templateTasks: MetalsTask[],
    projectTasks: MetalsTask[],
    templateIdToProjectTask: Map<string, MetalsTask>,
    projectIdToTemplateTask: Map<string, MetalsTask>,
): MetalsProjectTemplateLinkDiff | null {
    const templateTaskDict = new Map<string, MetalsTask>(templateTasks.map(task => [task.id, task]));
    const projectTaskDict = new Map<string, MetalsTask>(projectTasks.map(task => [task.id, task]));

    const templateNextTask = templateTask.nextTaskId ? templateTaskDict.get(templateTask.nextTaskId) : null;
    const projectNextTask = projectTask.nextTaskId ? projectTaskDict.get(projectTask.nextTaskId) : null;
    const matchedTemplateNextProjectTask = templateNextTask ? templateIdToProjectTask.get(templateNextTask.id) : null;
    const matchedProjectNextTemplateTask = projectNextTask ? projectIdToTemplateTask.get(projectNextTask.id) : null;

    const templateNextKey = matchedTemplateNextProjectTask?.id ?? (templateNextTask ? `unmatched-template:${templateNextTask.id}` : 'end');
    const projectNextKey = projectNextTask?.id ?? 'end';

    if (templateNextKey === projectNextKey) {
        return null;
    }

    return {
        templateTaskId: templateTask.id,
        projectTaskId: projectTask.id,
        taskLabel: toTaskLabel(projectTask),
        templateNextTaskLabel: templateNextTask ? toTaskLabel(templateNextTask) : 'end of route',
        projectNextTaskLabel: projectNextTask
            ? toTaskLabel(projectNextTask)
            : matchedProjectNextTemplateTask
                ? toTaskLabel(matchedProjectNextTemplateTask)
                : 'end of route',
    };
}

function addFieldChange(
    fieldChanges: MetalsProjectTemplateFieldDiff[],
    label: string,
    templateValue: unknown,
    projectValue: unknown,
) {
    const formattedTemplateValue = formatValue(templateValue);
    const formattedProjectValue = formatValue(projectValue);
    if (formattedTemplateValue === formattedProjectValue) return;
    fieldChanges.push({
        label,
        templateValue: formattedTemplateValue,
        projectValue: formattedProjectValue,
    });
}

function diffFiles(templateFiles: MetalsProjectFile[], projectFiles: MetalsProjectFile[]): MetalsProjectTemplateFileDiff {
    const templateByKey = new Map(templateFiles.map(file => [toFileKey(file), file]));
    const projectByKey = new Map(projectFiles.map(file => [toFileKey(file), file]));

    const added = projectFiles
        .filter(file => !templateByKey.has(toFileKey(file)))
        .map(toFileLabel)
        .sort();

    const removed = templateFiles
        .filter(file => !projectByKey.has(toFileKey(file)))
        .map(toFileLabel)
        .sort();

    const changed = templateFiles
        .filter(file => {
            const projectFile = projectByKey.get(toFileKey(file));
            return !!projectFile && toFileFullSignature(file) !== toFileFullSignature(projectFile);
        })
        .map(toFileLabel)
        .sort();

    return { added, removed, changed };
}

function hasFileChanges(fileChanges: MetalsProjectTemplateFileDiff): boolean {
    return fileChanges.added.length > 0 || fileChanges.removed.length > 0 || fileChanges.changed.length > 0;
}

function toTaskSummary(task: MetalsTask): MetalsProjectTemplateTaskSummary {
    return {
        taskId: task.id,
        label: toTaskLabel(task),
        taskType: task.type,
        files: task.files.map(toFileLabel).sort(),
    };
}

function toTaskLabel(task: MetalsTask): string {
    const firstFile = task.files[0]?.name;
    const details = task.details?.trim();
    if (firstFile) return `${task.type}: ${firstFile}`;
    if (details) return `${task.type}: ${truncate(details, 48)}`;
    return `${task.type} task`;
}

function toHardwareSignature(task: MetalsTask): string {
    const itemIds = toSortedString(task.hardwareItemIds ?? []);
    const qtyEntries = Object.entries(task.hardwareItemIdToQtyPerPartDict ?? {})
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([id, qty]) => `${id}:${qty}`)
        .join('|');
    return `${itemIds} / ${qtyEntries}`;
}

function toTypeSpecificSignature(task: MetalsTask): string {
    switch (task.type) {
        case MetalsTaskType.Laser:
            return [
                (task as MetalsTask_Laser).materialId,
                (task as MetalsTask_Laser).approximateWidth,
                (task as MetalsTask_Laser).approximateHeight,
            ].map(formatValue).join('|');
        case MetalsTaskType.PressBrake:
            return toSortedString((task as MetalsTask_PressBrake).bendRadiusIds ?? []);
        case MetalsTaskType.PowderCoating:
            return (task as MetalsTask_PowderCoating).powderId ?? '';
        default:
            return '';
    }
}

function toFileMatchingSignature(files: MetalsProjectFile[]): string {
    return files.map(toFileKey).sort().join('|');
}

function toFileFullSignature(file: MetalsProjectFile): string {
    return `${toFileKey(file)}:${file.size}:${file.downloadUrl}`;
}

function toFileKey(file: MetalsProjectFile): string {
    return `${file.type}:${file.name}`;
}

function toFileLabel(file: MetalsProjectFile): string {
    return `${file.name} (${file.type})`;
}

function toSortedString(value: string[] = []): string {
    return [...value].sort().join(', ');
}

function formatValue(value: unknown): string {
    if (value == null || value === '') return 'none';
    if (Array.isArray(value)) return value.map(formatValue).sort().join(', ');
    if (typeof value === 'number') return Number.isFinite(value) ? `${value}` : 'none';
    if (typeof value === 'boolean') return value ? 'yes' : 'no';
    return `${value}`;
}

function truncate(value: string, maxLength: number): string {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 3)}...`;
}
